/**
 * Temporary screen-scoped design tokens for **Settings → Support Tickets → a
 * single ticket**.
 *
 * The screen opens inside the Settings shell, so its card chrome, pills and
 * type steps are the same ones `SupportTickets.tokens.ts` uses for the list —
 * the detail view is the same surface, not a new one. Once every screen has
 * been re-skinned these get promoted into the MUI theme and this file is
 * deleted.
 */

import { contentCard, formField, inkfree, notification, signer, white } from "theme/colors";

export const viewTicketTokens = {
  color: {
    ink: signer.ink,
    /** Row labels, comment bodies, secondary button ink. */
    muted: signer.text.meta,
    body: signer.text.secondary,
    /** Timestamps, column labels, placeholder text, glyphs. */
    subtle: signer.text.faint,
    placeholder: formField.placeholderMuted,
    surface: white,
    cardBorder: contentCard.borderMuted,
    /** Rule between detail rows / above the editor footer. */
    divider: signer.border.divider,
    hairline: signer.border.light,
    fieldBorder: formField.border,
    fieldBg: formField.bg,
    /** Link text / hover — a thin accent, stays orange. */
    accent: signer.orange.main,
    accentHover: signer.orange.dark,
    /**
     * "Save" button fill — per the standing brand-colour rule this is the
     * app brand red, not `accent` above.
     */
    ctaBg: inkfree.main,
    ctaBgHover: inkfree.dark,
    onAccent: white,
    /** Author avatar for the signed-in user, and for everyone else. */
    selfAvatarBg: signer.ink,
    selfAvatarText: white,
    avatarBg: signer.border.light,
    avatarText: signer.text.secondary,
    /** Neutral pill, shared with the list's "Unassigned". */
    pillBg: signer.surface.muted,
    pillText: signer.text.meta,
    toolbarHoverBg: signer.border.divider,
  },

  font: {
    backLink: "12.5px",
    title: "20px",
    statusPill: "11.5px",
    meta: "12px",
    cardTitle: "15px",
    body: "13px",
    sectionLabel: "10.5px",
    attachment: "12.5px",
    rowLabel: "12px",
    rowValue: "12.5px",
    commentAuthor: "13px",
    timestamp: "11.5px",
    replyLink: "12px",
    avatar: "11px",
    editorHint: "12px",
    button: "12.5px",
  },

  weight: {
    strong: 700,
    heading: 800,
    medium: 600,
    regular: 500,
  },

  letterSpacing: {
    title: "-0.01em",
    sectionLabel: "0.09em",
  },

  radius: {
    card: "14px",
    control: "9px",
    editor: "10px",
    toolbarButton: "7px",
    pill: "999px",
  },

  shadow: {
    card: "0 1px 3px rgba(33, 21, 71, 0.04)",
  },

  /** Back link, ticket title with its status pill, and the meta line. */
  header: {
    backGap: "8px",
    backIconSize: 16,
    backMarginBottom: "14px",
    titleGap: "10px",
    metaMarginTop: "6px",
    marginBottom: "20px",
  },

  /** Two-column body: description + comments beside the details rail. */
  layout: {
    columnGap: "22px",
    railWidth: 338,
    cardGap: "20px",
    cardPadding: "24px 26px",
    cardTitleMarginBottom: "14px",
  },

  description: {
    lineHeight: 1.65,
    /** "ATTACHMENTS" label above the chip row. */
    labelMarginTop: "18px",
    labelMarginBottom: "10px",
    chipGap: "10px",
    chipPadding: "9px 12px",
    chipInnerGap: "9px",
    chipIconSize: 14,
  },

  comments: {
    /** Gap between the add-comment block and the thread. */
    threadMarginTop: "20px",
    threadGap: "18px",
    avatarSize: 30,
    avatarGap: "12px",
    /** Replies line up under their parent's body, past the avatar column. */
    replyIndent: "42px",
    /** Collapsed "Add a comment" affordance. */
    placeholderPadding: "12px 14px",
    authorGap: "8px",
    bodyMarginTop: "4px",
    bodyLineHeight: 1.6,
    attachmentMarginTop: "10px",
    replyLinkMarginTop: "8px",
    editorMarginTop: "12px",
    emptyPaddingY: "18px",
  },

  /** One "Status / Assignee / …" line in the details rail. */
  detailsRow: {
    paddingY: "11px",
    gap: "12px",
  },

  /** Rich-text comment box — the same chrome for a new comment and a reply. */
  editor: {
    toolbarPadding: "6px 10px",
    toolbarGroupGap: "8px",
    buttonSize: 28,
    buttonIconSize: 15,
    minHeight: "96px",
    contentPadding: "14px 16px",
    footerPadding: "12px 14px",
    footerGap: "10px",
    hintGap: "8px",
    hintIconSize: 14,
    buttonHeight: 34,
    buttonPaddingX: "18px",
    /** Matches the ceiling `CreateTicket` already enforces on attachments. */
    maxFileSizeLabel: "up to 2 MB",
    maxFileSizeBytes: 2 * 1024 * 1024,
  },
} as const;

/** Status pill fills, keyed by the API's ticket status values. */
export const ticketStatusTones: Record<string, { bg: string; color: string }> =
  {
    Open: { bg: inkfree.light, color: signer.orange.dark },
    InProcess: { bg: "#EAF2FC", color: "#1B5DAC" },
    "In Process": { bg: "#EAF2FC", color: "#1B5DAC" },
    Complete: { bg: notification.success.bg, color: notification.success.title },
  };

/** Priority pill fills, matching the dots the list view uses. */
export const ticketPriorityTones: Record<
  string,
  { bg: string; color: string }
> = {
  High: { bg: notification.error.bg, color: notification.error.title },
  Medium: { bg: inkfree.light, color: signer.orange.dark },
  Low: { bg: notification.success.bg, color: notification.success.title },
};

export default viewTicketTokens;
